// API Endpoints for Sources management
import { rivermeadowApi } from './api';

export const sources = rivermeadowApi.injectEndpoints({
    endpoints: (builder) => ({
        getSources: builder.query({
            query: ({ userOrg, size = 10, page = 0, sort, combined_search }) => ({
                url: `organizations/${userOrg}/sources`,
                method: 'GET',
                params: {
                    size,
                    page,
                    sort,
                    ...(combined_search && { combined_search }),
                },
            }),
            providesTags: ['SOURCES'],
        }),
        getSourceById: builder.query({
            query: (sourceId) => ({
                url: `sources/${sourceId}`,
                method: 'GET',
            }),
            providesTags: (result, error, id) => [{ type: 'SOURCES', id }],
        }),
        getSourceInspection: builder.query({
            query: (sourceId) => ({
                url: `sources/${sourceId}/inspection`,
                method: 'GET',
            }),
            providesTags: (result, error, id) => [{ type: 'SOURCE_INSPECTION', id }],
        }),
        getMoveGroups: builder.query({
            query: (userOrg) => ({
                url: `organizations/${userOrg}/movegroups`,
                method: 'GET',
                params: {
                    size: 1000,
                    sort: 'name,asc',
                },
            }),
            providesTags: ['MOVE_GROUPS'],
        }),
        createSource: builder.mutation({
            query: ({ userOrg, sourceData }) => ({
                url: `sources`,
                method: 'POST',
                body: sourceData,
            }),
            invalidatesTags: ['SOURCES'],
        }),
        updateSource: builder.mutation({
            query: ({ sourceId, sourceData }) => ({
                url: `sources/${sourceId}`,
                method: 'PUT',
                body: sourceData,
            }),
            invalidatesTags: ['SOURCES'],
        }),
        updateSourceTags: builder.mutation({
            query: ({ sourceId, tags, moveGroupId }) => {
                let url = `sources/${sourceId}?tags=${tags.join(',')}`;
                if (moveGroupId) {
                    url = `${url}&move_group_id=${moveGroupId}`;
                }
                return {
                    url: url,
                    method: 'PATCH'
                }
            },
            invalidatesTags: ['SOURCES'],
        }),
        deleteSources: builder.mutation({
            query: (sourceIds) => ({
                url: 'sources',
                method: 'DELETE',
                body: sourceIds, // Array of source IDs like ["5d019f44-100e-448b-b46f-4d7953662d8c"]
            }),
            invalidatesTags: (result, error, sourceIds) => [
                'SOURCES',
                ...sourceIds.map((id) => ({ type: 'SOURCES', id })),
            ],
        }),
        // Mutation for bulk operations that need to invalidate SOURCES cache
        bulkCreateSources: builder.mutation({
            query: ({ sourcesData, updateExisting = false }) => ({
                url: `sources/bulkcreate${updateExisting ? '?update_existing=true' : ''}`,
                method: 'POST',
                body: sourcesData,
            }),
            invalidatesTags: ['SOURCES'],
        }),
        // Trigger source inventory to fetch  datacenter/cloud metadata (datacenters, clusters, folders, etc.)
        triggerSourceDatacenterInventory: builder.mutation({
            query: ({ userOrg, applianceId, objectsToCollect, datacenterName }) => {
                const body = {
                    organization_id: userOrg,
                    appliance_id: applianceId,
                    objects_to_collect: objectsToCollect,
                    collect_cloud: true,
                    collect_vms: false,
                    add_sources: false,
                };

                // Add datacenter_name if provided (for fetching cluster/folder data for specific datacenter)
                if (datacenterName) {
                    body.datacenter_name = datacenterName;
                }

                return {
                    url: 'sourceinventories',
                    method: 'POST',
                    body,
                };
            },
            // Metadata fetch doesn't invalidate sources - it's just fetching cloud structure
            invalidatesTags: [],
        }),
        // Trigger source VMs inventory to collect VM sources with optional filters and move group assignment
        triggerSourceVMsInventory: builder.mutation({
            query: ({ userOrg, applianceId, filters, moveGroupInfo }) => {
                const body = {
                    organization_id: userOrg,
                    appliance_id: applianceId,
                    collect_cloud: false,
                    collect_vms: true,
                    add_sources: true,
                    collect_perf_metrics: true,
                };

                // Add vSphere filters if provided (datacenter, cluster, folder, etc.)
                if (filters && Object.keys(filters).length > 0) {
                    body.filters = filters;
                }

                // Add move group assignment info if provided
                if (moveGroupInfo) {
                    if (moveGroupInfo.move_group_id) {
                        body.move_group_id = moveGroupInfo.move_group_id;
                    }
                    if (moveGroupInfo.move_group_name) {
                        body.move_group_name = moveGroupInfo.move_group_name;
                    }
                    if (moveGroupInfo.target_migration_date) {
                        body.target_migration_date = moveGroupInfo.target_migration_date;
                    }
                    if (moveGroupInfo.assigned_resource_email) {
                        body.assigned_resource_email = moveGroupInfo.assigned_resource_email;
                    }
                }

                return {
                    url: 'sourceinventories',
                    method: 'POST',
                    body,
                };
            },
            // Invalidate sources cache after VM inventory completes
            invalidatesTags: ['SOURCE_INVENTORY'],
        }),
        getSourceInventoryStatus: builder.query({
            query: (inventoryId) => ({
                url: `sourceinventories/${inventoryId}`,
                method: 'GET',
            }),
            providesTags: (result, error, inventoryId) => [{ type: 'SOURCE_INVENTORY', id: inventoryId }],
        }),
    }),
});

export const {
    useGetSourcesQuery,
    useGetSourceByIdQuery,
    useLazyGetSourceByIdQuery,
    useGetSourceInspectionQuery,
    useLazyGetSourceInspectionQuery,
    useGetMoveGroupsQuery,
    useCreateSourceMutation,
    useUpdateSourceMutation,
    useUpdateSourceTagsMutation,
    useDeleteSourcesMutation,
    useBulkUpdateSourcesMutation,
    useBulkCreateSourcesMutation,
    useTriggerSourceDatacenterInventoryMutation,
    useTriggerSourceVMsInventoryMutation,
    useGetSourceInventoryStatusQuery,
    useLazyGetSourceInventoryStatusQuery,
} = sources;
